// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Wizard data service - stores each step's data as a JSON blob in db.wishes.
 */
import { db }          from '@/lib/db'
import { getMasterKey } from '@/services/profile'
import { generateUUID, nowISO } from '@/lib/utils'
import { SyncStatus, WishCategory, CompletionStatus } from '@/types'

async function encBlob(data: object, userId: string): Promise<{ blob: ArrayBuffer; iv: string }> {
  const enc = new TextEncoder().encode(JSON.stringify(data)).buffer as ArrayBuffer
  const key = getMasterKey(userId)
  if (key) {
    const ivArr = crypto.getRandomValues(new Uint8Array(12))
    const blob  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivArr }, key, enc)
    return { blob, iv: btoa(String.fromCharCode(...ivArr)) }
  }
  return { blob: enc, iv: '' }
}

async function decBlob<T>(blob: ArrayBuffer, ivB64: string, userId: string): Promise<T | null> {
  // V41-fix: ivB64 gevuld → data is versleuteld → key vereist
  // Nooit ciphertext als plain JSON parsen
  if (ivB64) {
    const key = getMasterKey(userId)
    if (!key) return null   // key ontbreekt na reload → null, GEEN overschrijving
    try {
      const iv  = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0))
      const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, blob)
      return JSON.parse(new TextDecoder().decode(dec)) as T
    } catch { return null }
  }
  // ivB64 leeg → plain opgeslagen
  try { return JSON.parse(new TextDecoder().decode(blob)) as T }
  catch { return null }
}

async function getRecord(userId: string, moduleKey: string) {
  return db.wishes.where('[userId+category]').equals([userId, WishCategory.OTHER])
    .filter((r) => (r as unknown as { moduleKey?: string }).moduleKey === moduleKey).first()
}

export async function wizardLoad<T>(userId: string, moduleKey: string): Promise<T | null> {
  const rec = await getRecord(userId, moduleKey)
  if (!rec?.encryptedContent) return null
  return decBlob<T>(rec.encryptedContent, rec.blobIv ?? '', userId)
}

export async function wizardSave(userId: string, moduleKey: string, data: object, filledCount?: number): Promise<void> {
  const { blob, iv } = await encBlob(data, userId)
  const status = (filledCount ?? 1) > 0 ? CompletionStatus.IN_PROGRESS : CompletionStatus.NOT_STARTED
  const existing = await getRecord(userId, moduleKey)
  if (existing) {
    await db.wishes.update(existing.id, { encryptedContent: blob, blobIv: iv, completionStatus: status, lastUpdatedAt: nowISO(), syncStatus: SyncStatus.PENDING })
  } else {
    const row = { id: generateUUID(), userId, category: WishCategory.OTHER, sortOrder: 50, completionStatus: status, lastUpdatedAt: nowISO(), createdAt: nowISO(), syncStatus: SyncStatus.PENDING, cloudSyncId: null, encryptedContent: blob, blobIv: iv }
    await db.wishes.add(Object.assign(row, { moduleKey }))
  }
}

// ── Partner ───────────────────────────────────────────────────────────────────
export interface PartnerData {
  hasPartner: string; name: string; lastName: string; dob: string
  relationForm: string; notes: string
  // V34.7: contactgegevens
  phone: string; email: string
  // V34.9: gestructureerd adres (consistent met PersonalData)
  livesAtSameAddress: string          // 'yes' | 'no' | ''
  partnerStreet:             string   // handmatig bij LAT; readonly van wizard_personal bij zelfde adres
  partnerHouseNumber:        string
  partnerHouseNumberAddition:string   // optioneel
  partnerPostalCode:         string
  partnerCity:               string
}
export const EMPTY_PARTNER: PartnerData = {
  hasPartner: '', name: '', lastName: '', dob: '',
  relationForm: '', notes: '',
  phone: '', email: '',
  livesAtSameAddress: '',
  partnerStreet: '', partnerHouseNumber: '', partnerHouseNumberAddition: '',
  partnerPostalCode: '', partnerCity: '',
}
export const loadPartner  = (u: string) => wizardLoad<PartnerData>(u, 'partner')
export const savePartner  = (u: string, d: PartnerData) => wizardSave(u, 'partner', d, d.hasPartner ? 1 : 0)

// ── Children ──────────────────────────────────────────────────────────────────
export interface ChildEntry { name: string; lastName?: string; dob: string; relation?: string; gender?: string; dobDay?: string; dobMonth?: string; dobYear?: string }
export interface GuardianData {
  appointed:         string   // 'yes' | 'no' | 'testament' | ''
  name:              string
  relation:          string
  phone:             string
  email:             string
  // V40-P2: extra velden (blob - geen schema-aanpassing)
  reserveName:       string
  reserveRelation:   string
  reservePhone:      string
  reserveEmail:      string
  testamentNotaris:  string
  testamentYear:     string
  testamentLocation: string
  notarialNotes:     string   // toelichting voor notaris
}
export const EMPTY_GUARDIAN: GuardianData = {
  appointed: '', name: '', relation: '', phone: '', email: '',
  reserveName: '', reserveRelation: '', reservePhone: '', reserveEmail: '',
  testamentNotaris: '', testamentYear: '', testamentLocation: '', notarialNotes: '',
}
export interface ChildrenData { hasChildren: string; children: ChildEntry[]; guardianNotes: string; guardian: GuardianData }
export const EMPTY_CHILDREN: ChildrenData = { hasChildren: '', children: [], guardianNotes: '', guardian: EMPTY_GUARDIAN }
export const loadChildren = (u: string) => wizardLoad<ChildrenData>(u, 'children')
export const saveChildren = (u: string, d: ChildrenData) => wizardSave(u, 'children', d, d.hasChildren ? 1 : 0)

// ── Pets ──────────────────────────────────────────────────────────────────────
export interface PetEntry {
  name: string; species: string; breed?: string; age: string
  vet: string; vetPhone: string
  caretaker: string; caretakerRelation: string
  food?: string; medication?: string; allergies?: string
  location?: string; locationDetails?: string
  behavior?: string; fireworkFear?: boolean; specialNotes: string
}
export interface PetsData { hasPets: string; pets: PetEntry[] }
export const EMPTY_PET: PetEntry = { name: '', species: '', breed: '', age: '', vet: '', vetPhone: '', caretaker: '', caretakerRelation: '', food: '', medication: '', allergies: '', location: '', locationDetails: '', behavior: '', fireworkFear: false, specialNotes: '' }
export const EMPTY_PETS: PetsData = { hasPets: '', pets: [] }
export const loadPets  = (u: string) => wizardLoad<PetsData>(u, 'pets')
export const savePets  = (u: string, d: PetsData) => wizardSave(u, 'pets', d, d.hasPets ? 1 : 0)

// ── Housing ───────────────────────────────────────────────────────────────────
export interface HousingData {
  type:                string
  address:             string   // legacy - gesynchroniseerd met street/houseNumber/city/postalCode
  street:              string
  houseNumber:         string
  houseNumberAddition: string   // V35-P2
  postalCode:          string
  city:                string
  useProfileAddress:   string   // 'yes' | 'no' | ''
  // Legacy veld - bewaard voor backward compatibility met bestaande opgeslagen data
  mortgage:            string
  landlord:            string
  keys:                string
  // V37: gestructureerde hypotheekvelden
  mortgageProvider:    string   // dropdown + vrij veld
  mortgageNumber:      string   // hypotheeknummer / leningnummer
  mortgageContact:     string   // contactpersoon of afdeling (optioneel)
  mortgagePhone:       string
  mortgageEmail:       string
  mortgageNotes:       string
  docsLocation:        string   // "Documenten aanwezig bij"
  // V37: overlijdensrisicoverzekering (ORV)
  hasOrv:              string   // 'yes' | 'no' | 'unknown'
  orvInsurer:          string
  orvPolicyNr:         string
  orvContact:          string
  orvNotes:            string
}
export const EMPTY_HOUSING: HousingData = {
  type: '', address: '',
  street: '', houseNumber: '', houseNumberAddition: '', postalCode: '', city: '',
  useProfileAddress: '',
  mortgage: '', landlord: '', keys: '',
  mortgageProvider: '', mortgageNumber: '', mortgageContact: '',
  mortgagePhone: '', mortgageEmail: '', mortgageNotes: '',
  docsLocation: '',
  hasOrv: '', orvInsurer: '', orvPolicyNr: '', orvContact: '', orvNotes: '',
}
export const loadHousing = (u: string) => wizardLoad<HousingData>(u, 'housing')
export const saveHousing = (u: string, d: HousingData) => wizardSave(u, 'housing', d, Object.values(d).filter(Boolean).length)

// ── Second Home ───────────────────────────────────────────────────────────────
export interface SecondHomeLocalContact {
  name: string; role: string; phone: string; email: string
}
export interface SecondHomeLocalGp {
  name: string; practice: string; phone: string; city: string
}
export interface SecondHomeDocuments {
  ownership: string; keys: string; alarmCodes: string; insurance: string
}
export interface SecondHomeData {
  hasSecondHome: string
  address:       string
  street:        string; houseNumber: string; postalCode: string; city: string; country: string
  type:          string   // 'vacation' | 'recreation' | 'apartment' | 'other'
  typeOther:     string
  mortgage:      string   // legacy - bewaard voor backward compat
  notes:         string
  localContact:  SecondHomeLocalContact
  localGp:       SecondHomeLocalGp
  documents:     SecondHomeDocuments
  // V38: gestructureerde hypotheekvelden tweede woning
  hasMortgage:         string   // 'yes' | 'no' | 'unknown'
  mortgageCountry:     string   // 'NL' | 'IT' | 'ES' | 'FR' | 'DE' | 'BE' | 'anders'
  mortgageProvider:    string   // dropdown NL of vrij veld buitenland
  mortgageProviderOther: string
  mortgageNumber:      string
  mortgageContact:     string
  mortgagePhone:       string
  mortgageEmail:       string
  mortgageNotes:       string
}
export const EMPTY_SECOND_HOME: SecondHomeData = {
  hasSecondHome: '', address: '',
  street: '', houseNumber: '', postalCode: '', city: '', country: '',
  type: '', typeOther: '', mortgage: '', notes: '',
  localContact: { name: '', role: '', phone: '', email: '' },
  localGp:      { name: '', practice: '', phone: '', city: '' },
  documents:    { ownership: '', keys: '', alarmCodes: '', insurance: '' },
  hasMortgage: '', mortgageCountry: '', mortgageProvider: '', mortgageProviderOther: '',
  mortgageNumber: '', mortgageContact: '', mortgagePhone: '', mortgageEmail: '', mortgageNotes: '',
}
export const loadSecondHome = (u: string) => wizardLoad<SecondHomeData>(u, 'secondHome')
export const saveSecondHome = (u: string, d: SecondHomeData) => wizardSave(u, 'secondHome', d, d.hasSecondHome ? 1 : 0)

// ── Bank Accounts ─────────────────────────────────────────────────────────────
export interface BankEntry {
  country: string; bankName: string; bankNameOther: string
  accountType: string; iban: string; coHolder: string; notes: string
}
export interface BankData { accounts: BankEntry[] }
export const EMPTY_BANK_ENTRY: BankEntry = { country: 'NL', bankName: '', bankNameOther: '', accountType: '', iban: '', coHolder: '', notes: '' }
export const EMPTY_BANK: BankData = { accounts: [] }
export const loadBank = (u: string) => wizardLoad<BankData>(u, 'bank')
export const saveBank = (u: string, d: BankData) => wizardSave(u, 'bank', d, d.accounts.length)

// ── Investments ───────────────────────────────────────────────────────────────
export interface InvestmentEntry {
  country: string; brokerName: string; brokerNameOther: string
  investmentType: string; hasLeverage: string
  isCrypto: boolean; cryptoPlatform: string; cryptoPlatformOther: string
  notes: string
}
export interface InvestmentsData { hasInvestments: string; investments: InvestmentEntry[] }
export const EMPTY_INVESTMENT: InvestmentEntry = { country: 'NL', brokerName: '', brokerNameOther: '', investmentType: '', hasLeverage: '', isCrypto: false, cryptoPlatform: '', cryptoPlatformOther: '', notes: '' }
export const EMPTY_INVESTMENTS: InvestmentsData = { hasInvestments: '', investments: [] }
export const loadInvestments = (u: string) => wizardLoad<InvestmentsData>(u, 'investments')
export const saveInvestments = (u: string, d: InvestmentsData) => wizardSave(u, 'investments', d, d.hasInvestments ? 1 : 0)

// ── Pension ───────────────────────────────────────────────────────────────────
export interface PensionData {
  hasPension: string; fundName: string; fundNameOther: string
  hasPartnerPension: string; hasSurvivorPension: string; notes: string
}
export interface PensionData {
  hasPension: string; fundName: string; fundNameOther: string
  hasPartnerPension: string; hasSurvivorPension: string; notes: string
  funds?: PensionFund[]   // V49-UAT: meerdere pensioenfondsen (terugwaarts compatibel met fundName/fundNameOther)
  hasPensionOverview?:      string  // V61: pensioenoverzicht opgeslagen? 'yes'|'no'|'unknown'
  pensionOverviewLocation?: string  // V61: waar nabestaanden het overzicht kunnen terugvinden
  hasSupplementaryPension?: string  // V61: aanvullend pensioen of lijfrente
  hasAnwProvision?:         string  // V61: nabestaandenvoorziening / ANW-hiaatverzekering
}
export interface PensionFund { fundName: string; fundNameOther: string; notes: string }
export const EMPTY_PENSION_FUND: PensionFund = { fundName: '', fundNameOther: '', notes: '' }
export const EMPTY_PENSION: PensionData = { hasPension: '', fundName: '', fundNameOther: '', hasPartnerPension: '', hasSurvivorPension: '', notes: '', funds: [], hasPensionOverview: '', pensionOverviewLocation: '', hasSupplementaryPension: '', hasAnwProvision: '' }
export const loadPension = (u: string) => wizardLoad<PensionData>(u, 'pension')
export const savePension = (u: string, d: PensionData) => wizardSave(u, 'pension', d, d.hasPension ? 1 : 0)

/** Terugwaarts compatibele lijst van pensioenfondsen: gebruikt funds[] als die er is,
 *  anders het oude enkele fonds (fundName/fundNameOther). Centrale bron voor UI, export en overzicht. */
export function pensionFundList(p: PensionData): PensionFund[] {
  if (p.funds && p.funds.length) return p.funds
  if (p.fundName || p.fundNameOther) return [{ fundName: p.fundName, fundNameOther: p.fundNameOther, notes: p.notes || '' }]
  return []
}
export function pensionFundNames(p: PensionData): string[] {
  return pensionFundList(p)
    .map(f => (f.fundName === 'Anders / onbekend' || f.fundName === 'Anders') ? (f.fundNameOther || 'Onbekend fonds') : (f.fundName || 'Onbekend fonds'))
    .filter(Boolean)
}

// ── Insurance ─────────────────────────────────────────────────────────────────
export interface InsuranceEntry {
  type: string; typeOther: string; insurer: string; insurerOther: string; policyNumber: string; notes: string
  uitvaartType:   string   // V37: 'natura' | 'kapitaal' | 'combinatie' | 'onbekend' - alleen bij type='Uitvaartverzekering'
  docsAvailable:  string   // V37: 'yes' | 'no' | 'unknown'
}
export interface InsuranceData { insurances: InsuranceEntry[] }
export const EMPTY_INSURANCE_ENTRY: InsuranceEntry = { type: '', typeOther: '', insurer: '', insurerOther: '', policyNumber: '', notes: '', uitvaartType: '', docsAvailable: '' }
export const EMPTY_INSURANCE: InsuranceData = { insurances: [] }
export const loadInsurance = (u: string) => wizardLoad<InsuranceData>(u, 'insurance')
export const saveInsurance = (u: string, d: InsuranceData) => wizardSave(u, 'insurance', d, d.insurances.length)

// ── Donor ─────────────────────────────────────────────────────────────────────
export interface DonorData { registered: string; notes: string }
export const EMPTY_DONOR: DonorData = { registered: '', notes: '' }
export const loadDonor = (u: string) => wizardLoad<DonorData>(u, 'donor')
export const saveDonor = (u: string, d: DonorData) => wizardSave(u, 'donor', d, d.registered ? 1 : 0)

// ── Reference data ────────────────────────────────────────────────────────────
export const NL_BANKS = ['ABN AMRO','ING','Rabobank','SNS Bank','ASN Bank','RegioBank','Triodos','Bunq','Knab','Openbank','Revolut (NL)','N26','Buitenlandse bank','Anders']
export const BANK_ACCOUNT_TYPES = ['Betaalrekening','Spaarrekening','Deposito','Zakelijke rekening','Beleggingsrekening','Hypotheekrekening','Anders']
export const NL_BROKERS = ['ABN AMRO (MeesPierson)','ING Beleggen','Rabobank Effecten','DeGiro','Saxo Bank','Flatex','BinckBank/Saxo','eToro','Interactive Brokers','Lynx','Bolero','Anders']
export const INTL_BROKERS = ['Interactive Brokers','eToro','Degiro','Saxo Bank','Schwab','TD Ameritrade','Binance','Coinbase','Kraken','Anders']
export const INVESTMENT_TYPES = ['Aandelen','Obligaties','ETF / Indexfonds','Beleggingsfonds','Opties / Futures','Turbo\'s / Sprinters','CFD\'s','Vastgoed (REIT)','Anders']
export const CRYPTO_PLATFORMS = ['Bitvavo','Coinbase','Binance','Kraken','Ledger (hardware)','MetaMask (wallet)','Anders']
export const NL_PENSION_FUNDS = ['ABP','PFZW','PME','PMT','Pensioenfonds Zorg en Welzijn','Bedrijfstakpensioenfonds Bouw','Pensioenfonds Metaal','Pensioenfonds Detailhandel','Pensioenfonds Horeca','NN Pensioenfonds','Aegon Pensioen','Nationale-Nederlanden','ASR Pensioen','Zwitserleven','Mijn werkgever regelt het','Anders / onbekend']
export const INSURANCE_TYPES = ['Levensverzekering','Overlijdensrisicoverzekering','Uitvaartverzekering','Zorgverzekering','Aanvullende verzekering','AOV (arbeidsongeschiktheid)','Woonverzekering','Inboedelverzekering','Autoverzekering','Rechtsbijstandverzekering','Reisverzekering','Anders']
export const NL_INSURERS = ['Nationale-Nederlanden','Centraal Beheer','OHRA','Interpolis','Zilveren Kruis','CZ','Menzis','VGZ','DSW','a.s.r.','Aegon','Allianz','Delta Lloyd','Generali','DELA','Monuta','Ardanta','Yarden / a.s.r.','Klaverblad','Twenthe','Nuvema','OVZ','Uitvaartzorg Nederland','Anders']

// V54: verzekeraars voor de overlijdensrisicoverzekering (ORV). 'Anders' wordt
// door SelectWithOther toegevoegd; daarom hier niet opnemen.
export const ORV_INSURERS = [
  'Nationale Nederlanden', 'ASR', 'Aegon', 'Allianz', 'Centraal Beheer', 'Interpolis',
  'Achmea', 'Univé', 'Reaal', 'Zwitserleven', 'De Goudse', 'Scildon', 'Dela', 'Monuta',
  'Klaverblad', "Onderlinge 's-Gravenhage", 'Lloyds Bank', 'Rabobank', 'ABN AMRO', 'ING',
]
// ── Testament details (E) ──────────────────────────────────────────────────────
export interface TestamentDetails {
  notarisName:      string
  notarisCity:      string
  notarisStraat:      string
  notarisHuisnummer:  string
  notarisPostcode:    string
  notarisPhone:       string
  notarisEmail:       string
  notarisWebsite:     string
  year:             string
  documentLocation: string
}
export const EMPTY_TESTAMENT_DETAILS: TestamentDetails = {
  notarisName: '', notarisCity: '', notarisStraat: '', notarisHuisnummer: '', notarisPostcode: '',
  notarisPhone: '', notarisEmail: '', notarisWebsite: '', year: '', documentLocation: '',
}
export const loadTestamentDetails  = (u: string) => wizardLoad<TestamentDetails>(u, 'testament_details')
export const saveTestamentDetails  = (u: string, d: TestamentDetails) =>
  wizardSave(u, 'testament_details', d, Object.values(d).filter(Boolean).length)

// ── Levenstestament details (F) ────────────────────────────────────────────────
export interface LivingWillDetails {
  notarisName:      string
  notarisCity:      string
  year:             string
  documentLocation: string
  // V41: volmachthouder
  proxyName:        string
  proxyRelation:    string
  proxyPhone:       string
  proxyEmail:       string
  proxyScope:       string   // 'financial' | 'medical' | 'both' | ''
}
export const EMPTY_LIVING_WILL_DETAILS: LivingWillDetails = {
  notarisName: '', notarisCity: '', year: '', documentLocation: '',
  proxyName: '', proxyRelation: '', proxyPhone: '', proxyEmail: '', proxyScope: '',
}
export const loadLivingWillDetails = (u: string) => wizardLoad<LivingWillDetails>(u, 'living_will_details')
export const saveLivingWillDetails = (u: string, d: LivingWillDetails) =>
  wizardSave(u, 'living_will_details', d, Object.values(d).filter(Boolean).length)

// ── Koopwoning uitgebreid (G) ───────────────────────────────────────────────────
